import { QueryCache, MutationCache, QueryClient, QueryFunction } from "@tanstack/react-query";
import { isUnauthorizedError, notifyUnauthorized } from "./auth-events";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // A 401 on an authenticated request means the session is (probably) gone —
    // broadcast so useAuth can confirm and route to the timed-out re-auth surface.
    if (res.status === 401) notifyUnauthorized();
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

function createAuthHeaders(includeContentType = false) {
  const headers: Record<string, string> = {};
  
  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }
  
  return headers;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: createAuthHeaders(!!data),
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      headers: createAuthHeaders(),
      credentials: "include",
    });

    // A 401 means the session is (probably) gone. We still return null rather
    // than throwing (to avoid an auth-cascade of error states), but we now also
    // broadcast the unauthorized signal so useAuth can confirm against /auth/me
    // and route the user to the "session timed out" surface instead of leaving
    // them inside the app with every fetch silently returning null.
    if (res.status === 401) {
      notifyUnauthorized();
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Catch 401s from queries/mutations that supply their own queryFn (e.g.
// useProjects, useProjectByShortId, useScopingSnapshot, useRenameProject) — they
// throw on a non-OK response instead of going through getQueryFn, so the global
// cache error handlers are the single chokepoint that broadcasts the signal for
// every authenticated request, regardless of how the hook fetches.
const queryCache = new QueryCache({
  onError: (error) => {
    if (isUnauthorizedError(error)) notifyUnauthorized();
  },
});
const mutationCache = new MutationCache({
  onError: (error) => {
    if (isUnauthorizedError(error)) notifyUnauthorized();
  },
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }), // Changed to returnNull to prevent auth cascade
      refetchInterval: false,
      refetchOnWindowFocus: true, // Refetch on window focus to get fresh data
      staleTime: 0, // Always consider data stale to fetch fresh data
      retry: (failureCount, error) => {
        // Don't retry on 401 errors to prevent auth spam
        if (error?.message?.includes('401')) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
